"use client";

import { useState, useRef, useEffect } from "react";
import { signIn } from "next-auth/react";

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), ms)
    ),
  ]);
}

export default function PinLoginForm() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fallback, setFallback] = useState<{ email: string; pin: string; csrfToken: string } | null>(null);
  const fallbackRef = useRef<HTMLFormElement>(null);

  // Auto-submit the hidden form when fallback data is set
  useEffect(() => {
    if (fallback && fallbackRef.current) {
      fallbackRef.current.submit();
    }
  }, [fallback]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const form = new FormData(e.currentTarget);
    const email = form.get("email") as string;
    const pin = form.get("pin") as string;

    if (!email || !pin) {
      setError("Ingresa email y PIN.");
      setLoading(false);
      return;
    }

    try {
      const result = await withTimeout(
        signIn("pin-login", { email, pin, redirect: false }),
        15000
      );

      if (result?.ok) {
        // Verify the session cookie actually persisted (WKWebView fetch() quirk)
        const sess = await fetch("/api/auth/session")
          .then((r) => r.json())
          .catch(() => null);

        if (sess?.user) {
          // Cookie persisted — navigate normally
          window.location.href = "/";
        } else {
          // Cookie didn't persist (WKWebView) — fall back to full-page form POST
          // which sets cookies via standard navigation instead of fetch()
          const csrf = await fetch("/api/auth/csrf")
            .then((r) => r.json())
            .then((d) => d.csrfToken || "")
            .catch(() => "");
          setFallback({ email, pin, csrfToken: csrf });
        }
      } else {
        setError("PIN incorrecto o email sin acceso.");
        setLoading(false);
      }
    } catch (err) {
      const msg = err instanceof Error && err.message === "timeout"
        ? "Tiempo agotado. Verifica tu conexión."
        : "Error de conexión. Intenta de nuevo.";
      setError(msg);
      setLoading(false);
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-2">
        {error && (
          <p className="text-center text-sm text-red-400">{error}</p>
        )}
        <input
          name="email"
          type="email"
          placeholder="Email"
          required
          className="w-full rounded-xl border border-border bg-surface-hover px-4 py-3 text-sm text-text"
        />
        <input
          name="pin"
          type="password"
          placeholder="PIN"
          inputMode="numeric"
          required
          className="w-full rounded-xl border border-border bg-surface-hover px-4 py-3 text-sm text-text"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-xl bg-accent px-4 py-3 text-sm font-medium text-white transition hover:bg-accent/90 disabled:opacity-50"
        >
          {loading ? "Entrando..." : "Entrar"}
        </button>
      </form>

      {/* Hidden form for WKWebView fallback — full-page POST sets cookies reliably */}
      {fallback && (
        <form
          ref={fallbackRef}
          method="POST"
          action="/api/auth/callback/pin-login"
          style={{ display: "none" }}
        >
          <input name="email" value={fallback.email} readOnly />
          <input name="pin" value={fallback.pin} readOnly />
          <input name="csrfToken" value={fallback.csrfToken} readOnly />
          <input name="callbackUrl" value="/" readOnly />
        </form>
      )}
    </>
  );
}
